import { queryClient } from '@/query-client';
import DownOutlined from '@ant-design/icons/DownOutlined';
import SyncOutlined from '@ant-design/icons/SyncOutlined';
import EmptySvg from '@images/undraw/new-message.svg';
import App from 'antd/es/app';
import Button from 'antd/es/button';
import Empty from 'antd/es/empty';
import { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import {
  useConversationAgentRuns,
  useConversationMessages,
} from './Conversation.hooks';
import {
  Container,
  ConversationLayout,
  EmptyStateContainer,
  InputContainer,
  MessageWrapper,
  MessagesContainer,
  SuggestionContainer,
} from './Conversations.styles';
import Message from './components/Message';
import MessageInput from './components/MessageInput';

import CenterSpin from '@components/CenterSpin';
import { AGENT_RUNS_QUERY_KEY } from '@queries/agent-runs';
import {
  createConversationMessage,
  deleteConversationMessage,
} from '@queries/conversation-messages';
import { useWebsocketPubSub } from '@queries/websockets';

const Conversation = ({ conversationId }: { conversationId: string }) => {
  const { subscribe, unsubscribe } = useWebsocketPubSub();
  const { message } = App.useApp();
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const isFirstLoadRef = useRef(true);
  const [showJumpToBottom, setShowJumpToBottom] = useState(false);
  const [requestedModel, setRequestedModel] = useState<
    'basic' | 'standard' | 'thorough'
  >('standard');

  const [isSendingMessage, setIsSendingMessage] = useState(false);

  // The list of messages currently in the conversation
  const { conversationMessages, isStreaming, isLoading } =
    useConversationMessages({
      conversationId,
    });

  // Fetch agent runs associated with this conversation
  const { agentRuns } = useConversationAgentRuns(conversationId);

  // Check if user is at the bottom of the conversation
  const isUserAtBottom = useCallback(() => {
    if (!messagesContainerRef.current) return true;

    const { scrollTop, scrollHeight, clientHeight } =
      messagesContainerRef.current;
    // Consider "at bottom" if within 50px of the bottom
    return scrollHeight - scrollTop - clientHeight <= 50;
  }, []);

  // Update button visibility based on scroll position
  const updateButtonVisibility = useCallback(() => {
    if (messagesContainerRef.current) {
      const shouldShow = !isUserAtBottom();
      setShowJumpToBottom(shouldShow);
    }
  }, [isUserAtBottom]);

  // Handle scroll to bottom functionality
  const scrollToBottom = useCallback(() => {
    if (messagesContainerRef.current) {
      const { scrollHeight, clientHeight } = messagesContainerRef.current;
      messagesContainerRef.current.scrollTop = scrollHeight - clientHeight;

      // After scrolling to bottom, ensure the button is hidden
      setShowJumpToBottom(false);
    }
  }, []);

  // Subscribe to agent run updates for those present
  useEffect(() => {
    const subscriptionIds = agentRuns
      .filter((run) => run.agentRunId)
      .map((run) =>
        subscribe(
          {
            options: {
              resourceType: 'agent_run',
              resourceId: run.agentRunId,
            },
            topic: 'resource_updated',
          },
          () => {
            void queryClient.invalidateQueries({
              queryKey: [AGENT_RUNS_QUERY_KEY],
            });
          },
        ),
      );

    return () => {
      subscriptionIds.forEach((id) => unsubscribe(id));
    };
    // NOTE: do not include subscribe/unsubscribe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentRuns]);

  // Handle initial scroll once messages are loaded
  useEffect(() => {
    if (
      isFirstLoadRef.current &&
      conversationMessages.length > 0 &&
      messagesContainerRef.current
    ) {
      const { scrollHeight, clientHeight } = messagesContainerRef.current;
      const container = messagesContainerRef.current;

      container.style.scrollBehavior = 'auto';
      container.scrollTop = scrollHeight - clientHeight;
      container.style.scrollBehavior = 'smooth';
      isFirstLoadRef.current = false;

      // After initial scroll, check button visibility
      updateButtonVisibility();
    }
  }, [conversationMessages, updateButtonVisibility]);

  // Set up scroll event listener to track if user is at bottom
  useEffect(() => {
    // Wait for DOM to be fully rendered
    const timeoutId = setTimeout(() => {
      const currentContainer = messagesContainerRef.current;
      if (!currentContainer) return;

      // Direct DOM event handler for testing
      const handleScroll = () => {
        const shouldShow = !isUserAtBottom();
        setShowJumpToBottom(shouldShow);
      };

      // Try both ways of attaching the event
      currentContainer.onscroll = handleScroll;
      currentContainer.addEventListener('scroll', handleScroll);

      // Force an initial check
      handleScroll();
    }, 500);

    const currentContainer = messagesContainerRef.current;
    return () => {
      clearTimeout(timeoutId);
      if (currentContainer) {
        currentContainer.onscroll = null;
        currentContainer.removeEventListener('scroll', () => {});
      }
    };
  }, [isUserAtBottom]);

  // Check if we should show the jump to bottom button when messages change
  useEffect(() => {
    if (messagesContainerRef.current && conversationMessages.length > 0) {
      const wasAtBottom = isUserAtBottom();

      if (wasAtBottom) {
        // If user was already at bottom, scroll to bottom and hide button
        requestAnimationFrame(() => {
          scrollToBottom();
          setShowJumpToBottom(false);
        });
      } else {
        // If user was not at bottom, show the button
        setShowJumpToBottom(true);
      }
    }
  }, [conversationMessages, isUserAtBottom, scrollToBottom]);

  // Dedicated effect for initial button visibility
  useEffect(() => {
    // Wait for the component to be fully mounted and rendered
    const timeoutId = setTimeout(() => {
      if (messagesContainerRef.current && conversationMessages.length > 0) {
        // Force a scroll event to trigger visibility check
        const event = new Event('scroll');
        messagesContainerRef.current.dispatchEvent(event);

        // Also explicitly check visibility
        updateButtonVisibility();

        // If we're not at the bottom, force the button to show
        if (!isUserAtBottom()) {
          setShowJumpToBottom(true);
        }
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [conversationMessages.length, isUserAtBottom, updateButtonVisibility]);

  // Simplify the resize observer to just check if we're at bottom
  useEffect(() => {
    const currentContainer = messagesContainerRef.current;
    if (!currentContainer) return;

    const resizeObserver = new ResizeObserver(() => {
      updateButtonVisibility();
    });

    resizeObserver.observe(currentContainer);

    return () => {
      resizeObserver.disconnect();
    };
  }, [updateButtonVisibility]);

  if (!conversationId) {
    return <Empty description="No conversation selected" />;
  }

  if (isLoading) {
    return <CenterSpin />;
  }

  return (
    <ConversationLayout>
      <Container>
        <MessagesContainerWrapper>
          <MessagesContainer ref={messagesContainerRef}>
            {conversationMessages.length === 0 ? (
              <EmptyStateContainer>
                <Empty
                  image={EmptySvg}
                  description="No messages yet. Ask a question or start by explaining a problem you're trying to solve."
                />
              </EmptyStateContainer>
            ) : (
              conversationMessages.map(
                ({
                  conversationMessageId,
                  role,
                  content,
                  thoughts,
                  isStreaming,
                  requestedModel,
                  createdAt,
                  tasks,
                }) => {
                  return (
                    <MessageWrapper
                      key={conversationMessageId}
                      $role={role as 'user' | 'assistant' | 'system'}
                    >
                      <Message
                        messageId={conversationMessageId}
                        role={role as 'user' | 'assistant' | 'system'}
                        content={content.text}
                        thoughts={thoughts}
                        createdAt={createdAt.toISOString()}
                        onDeleteMessage={async () => {
                          try {
                            await deleteConversationMessage(
                              conversationMessageId,
                            );
                          } catch {
                            void message.error('Failed to delete message');
                          }
                        }}
                        isStreaming={isStreaming}
                        tasks={tasks}
                        requestedModel={requestedModel}
                      />
                    </MessageWrapper>
                  );
                },
              )
            )}

            {/* Research update suggestion */}
            {conversationMessages.length > 0 &&
              (() => {
                const lastMessage =
                  conversationMessages[conversationMessages.length - 1];

                // Check if last message is from assistant and has completed tasks
                if (
                  lastMessage.role === 'assistant' &&
                  lastMessage.tasks &&
                  lastMessage.tasks.length > 0 &&
                  !isStreaming
                ) {
                  // Get agent runs for the tasks
                  const lastMessageTaskIds = lastMessage.tasks
                    .filter((task) => task.taskType === 'agent_run')
                    .map((task) => task.agentRunId);

                  // Find the agent runs for these tasks
                  const relevantAgentRuns = agentRuns.filter((run) =>
                    lastMessageTaskIds.includes(run.agentRunId),
                  );

                  // Check if there are any completed tasks (succeeded)
                  const hasCompletedTasks = relevantAgentRuns.some(
                    (run) => run.succeededAt,
                  );

                  if (hasCompletedTasks) {
                    return (
                      <SuggestionContainer>
                        <Button
                          type="primary"
                          ghost
                          onClick={async () => {
                            setIsSendingMessage(true);
                            try {
                              await createConversationMessage({
                                role: 'user',
                                content: {
                                  text: 'Update me on the latest tasks you have completed.',
                                },
                                conversationId,
                                contentType: 'plaintext',
                                requestedModel,
                              });
                            } catch {
                              void message.error('Failed to send message');
                            } finally {
                              setIsSendingMessage(false);
                            }
                          }}
                          disabled={isSendingMessage}
                        >
                          <SyncOutlined /> Update me on the latest tasks you
                          have completed
                        </Button>
                      </SuggestionContainer>
                    );
                  }
                }

                return null;
              })()}
          </MessagesContainer>

          {/* Jump to bottom button with fade animation */}
          <JumpToBottomButtonContainer $visible={showJumpToBottom}>
            <Button
              type="primary"
              icon={<DownOutlined />}
              onClick={scrollToBottom}
              size="middle"
            >
              Jump To Bottom
            </Button>
          </JumpToBottomButtonContainer>
        </MessagesContainerWrapper>

        <InputContainer>
          <MessageInput
            onSubmit={async (input) => {
              if (!input.value) {
                return;
              }
              setIsSendingMessage(true);
              const loadingMessage = message.loading('Sending message...');
              try {
                await createConversationMessage({
                  role: 'user',
                  content: { text: input.value },
                  conversationId,
                  contentType: 'plaintext',
                  requestedModel: input.model as
                    | 'basic'
                    | 'standard'
                    | 'thorough',
                });
                loadingMessage();
              } catch {
                loadingMessage();
                void message.error('Failed to send message');
              } finally {
                setIsSendingMessage(false);
              }
            }}
            disabled={isSendingMessage}
            requestedModel={requestedModel}
            setRequestedModel={setRequestedModel}
          />
        </InputContainer>
      </Container>
    </ConversationLayout>
  );
};

export default Conversation;

// Add a wrapper for the messages container to position the button
const MessagesContainerWrapper = styled.div`
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

// Add the styled components for the Jump To Bottom button
const JumpToBottomButtonContainer = styled.div<{ $visible: boolean }>`
  position: absolute;
  bottom: 16px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  z-index: 20;
  pointer-events: none;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  visibility: ${({ $visible }) => ($visible ? 'visible' : 'hidden')};
  transition:
    opacity 0.3s ease,
    visibility 0.3s ease;

  .ant-btn {
    pointer-events: auto;
    border-radius: 20px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }
`;
